import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate } from "react-router-dom";
import { Users, Shield, ShieldCheck, HardDrive } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AdminReviews } from "../components/AdminReviews";
import { AdminActivity } from "../components/admin/AdminActivity";
import { AdminUsersTable } from "../components/admin/AdminUsersTable";

function Stat({ icon: Icon, value, label, tone }) {
  return (
    <div className={`rounded-xl border p-4 ${tone}`}>
      <div className="flex items-center justify-between">
        <Icon className="h-5 w-5" />
        <span className="font-head text-3xl font-extrabold">{value ?? "—"}</span>
      </div>
      <p className="mt-1 text-xs font-medium">{label}</p>
    </div>
  );
}

const STATS = [
  ["users", Users, "Utilisateurs", "border-sky-800 bg-sky-950/40 text-sky-400"],
  ["admins", Shield, "Administrateurs", "border-violet-800 bg-violet-950/40 text-violet-400"],
  ["mfa", ShieldCheck, "Comptes avec MFA", "border-emerald-800 bg-emerald-950/40 text-emerald-400"],
  ["profiles", HardDrive, "Profils matériel", "border-amber-800 bg-amber-950/40 text-amber-400"],
];

const adminQuery = (key, path, pick, enabled) => ({
  queryKey: [key],
  queryFn: async () => pick((await api.get(path)).data),
  enabled,
});

export default function Admin() {
  const { user, ready } = useAuth();
  const qc = useQueryClient();
  const isAdmin = !!user && user.role === "admin";

  const stats = useQuery(adminQuery("admin-stats", "/admin/stats", (d) => d, isAdmin));
  const users = useQuery(adminQuery("admin-users", "/admin/users", (d) => d.users, isAdmin));
  const activity = useQuery(adminQuery("admin-activity", "/admin/activity", (d) => d.activity, isAdmin));

  if (ready && !user) return <Navigate to="/login" replace />;
  if (ready && user && !isAdmin) return <Navigate to="/" replace />;
  if (!user) return null;

  const mutate = async (fn, message) => {
    try {
      await fn();
      toast.success(message);
      ["admin-users", "admin-stats", "admin-activity", "admin-reviews"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Échec");
    }
  };
  const del = (id) => mutate(() => api.delete(`/admin/users/${id}`), "Utilisateur supprimé.");
  const setRole = (id, role) =>
    mutate(() => api.post(`/admin/users/${id}/role`, { role }), role === "admin" ? "Promu administrateur." : "Rétrogradé utilisateur.");

  return (
    <div className="mx-auto max-w-6xl px-4 py-10 md:px-8">
      <div className="mb-6 flex items-center gap-2">
        <Shield className="h-6 w-6 text-primary" />
        <h1 className="font-head text-3xl font-extrabold tracking-tight">Administration</h1>
      </div>
      <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-4">
        {STATS.map(([k, icon, label, tone]) => <Stat key={k} icon={icon} value={stats.data?.[k]} label={label} tone={tone} />)}
      </div>
      <AdminUsersTable users={users.data || []} fetching={users.isFetching} meId={user.id} onDelete={del} onRole={setRole} />
      <AdminActivity items={activity.data || []} />
      <AdminReviews />
    </div>
  );
}
